import { ReactNode, useMemo } from 'react';

import { useAvailableServicesData } from '@/dataAccess/rest';

import {
  ALL_CLUSTER_CAPABILITIES_SET,
  buildSiteDeviceServicesAvailability,
  buildStrictCapabilitySetFromPods,
} from './availableServicesProvider/availableServices.functions.ts';
import { AvailableServicesContext } from './availableServicesProvider/AvailableServicesContext.ts';

type AvailableServicesProviderProps = {
  children: ReactNode;
};

/** Site-scoped service availability shared across side menu, shortcuts, widgets, and device config. */
export const AvailableServicesProvider = ({ children }: AvailableServicesProviderProps) => {
  const {
    data: podsNames,
    isLoading,
    isFetching,
    isFetched,
    isSuccess,
    isError,
  } = useAvailableServicesData();

  const clusterAvailableServiceSet = useMemo(() => {
    const noPodsOrError = isError || (isSuccess && (podsNames?.length ?? 0) === 0);
    if (noPodsOrError) return ALL_CLUSTER_CAPABILITIES_SET;
    if (podsNames === undefined) return null;
    return buildStrictCapabilitySetFromPods(podsNames);
  }, [isError, isSuccess, podsNames]);

  /** Keyed only on the capability set so query flags (`isFetching`, etc.) do not churn `availableServices`. */
  const deviceConfigServicesAvailability = useMemo(
    () => buildSiteDeviceServicesAvailability(clusterAvailableServiceSet),
    [clusterAvailableServiceSet]
  );

  const value = useMemo(
    () => ({
      isLoading,
      isFetching,
      isFetched,
      isSuccess,
      isError,
      clusterAvailableServiceSet,
      deviceConfigServicesAvailability,
    }),
    [
      clusterAvailableServiceSet,
      deviceConfigServicesAvailability,
      isError,
      isFetched,
      isFetching,
      isLoading,
      isSuccess,
    ]
  );

  return <AvailableServicesContext value={value}>{children}</AvailableServicesContext>;
};
